import { maybeQuoteLabelName, ParserTerms, parser, walkBackward } from '@chronosphereio/editors/prometheus';
import { SyntaxNodeRef } from '@lezer/common';
import {
  DEFAULT_INJECT_OPERATOR,
  InjectVariableOptions,
  LabelMatcherInjection,
  ListVariableDefinition,
  PrometheusLabelNamesVariableOptions,
  PrometheusLabelValuesVariableOptions,
  parseTemplateVariableNames,
  TextVariableDefinition,
  VariableDefinition,
} from '@/core';
import { GlobalFilter } from '@/plugin-system';

/**
 * Strips full-line PromQL comments (lines whose first non-whitespace character is `#`)
 * so that regex-based variable extraction does not pick up variables mentioned only
 * in comments. This intentionally does not handle trailing comments on expression
 * lines because the Lezer parser already ignores those in AST traversal.
 */
export function stripPromQLComments(query: string): string {
  return query
    .split('\n')
    .filter((line) => !line.trimStart().startsWith('#'))
    .join('\n');
}

const {
  TemplateVariable,
  StringLiteral,
  MatrixSelector,
  LabelMatcher,
  EqlRegex,
  VectorSelector,
  GroupingLabels,
  AggregateExpr,
  Identifier,
  LabelMatchers,
  AggregateModifier,
  Without,
  FunctionCall,
  LabelName,
  Duration,
} = ParserTerms;

/**
 * This function will return a list of variable suggestions for a given PromQL query
 */
export function getVariableSuggestions(query: string) {
  const queryVariables = parseTemplateVariableNames(stripPromQLComments(query));

  const ast = parser.parse(query);
  const suggestions: Record<string, VariableDefinition> = {};

  ast.iterate({
    enter(n) {
      const value = getNodeText(n, query);
      const variableName = getVariableNameFromString(value);
      // If we can't find a variable name, we don't need to continue
      if (!variableName) {
        return;
      }

      if (n.type.is(TemplateVariable)) {
        // We are in a step interval
        // eg: sum(rate(my_metric[$interval]))
        if (n.node.parent?.type.is(MatrixSelector)) {
          suggestions[variableName] = createStepIntervalVariable(variableName);
        }

        // We are in a group by expression
        // eg: sum by (job) ($label)
        if (n.node.parent?.type.is(GroupingLabels)) {
          const p = walkBackward(n.node, AggregateExpr);
          const matchers: string[] = [];
          p?.toTree().iterate({
            enter(newNode) {
              if (newNode.type.is(Identifier)) {
                matchers.push(getNodeText(newNode, query));
              }
            },
          });
          suggestions[variableName] = createPromLabelNamesVariable(variableName, matchers);
        }
      }

      // We are in a label matcher
      // eg {job="$node"}
      if (n.type.is(StringLiteral)) {
        // print the string value
        const value = getNodeText(n, query);
        const variableName = getVariableNameFromString(value);
        if (variableName && n.node.parent?.type.is(LabelMatcher)) {
          const metricIdNode = walkBackward(n.node, VectorSelector)?.firstChild;

          const isMulti = n.node.prevSibling?.firstChild?.type.is(EqlRegex);
          const labelNameNode = n.node.prevSibling?.prevSibling;
          const labelName = labelNameNode ? getNodeText(labelNameNode, query) : undefined;

          if (!labelName) {
            return;
          }

          suggestions[variableName] = createPromLabelValuesVariable(variableName, labelName, {
            isMulti,
            matchers: metricIdNode ? [getNodeText(metricIdNode, query) + '{}'] : undefined,
          });
        }
      }
    },
  });

  return queryVariables.map((v) => {
    // If we have a suggestion for the variable, use it. Otherwise, use the default
    return suggestions[v] || defaultVariableConfig(v);
  });
}

/**
 * Extracts label names that use a specific variable in a PromQL query.
 * @param query The PromQL query to search
 * @param variableName The variable name to look for (without the $ prefix)
 * @returns Array of label names that use this variable
 */
export function extractLabelNamesForVariableFromPromQL(query: string, variableName: string): string[] {
  if (!query || !variableName) {
    return [];
  }

  const labelNames = new Set<string>();
  const ast = parser.parse(query);

  ast.iterate({
    enter(n) {
      // Look for StringLiteral nodes inside LabelMatcher
      // eg {job="$node"} - StringLiteral contains "$node"
      if (n.type.is(StringLiteral)) {
        const value = getNodeText(n, query);
        const foundVariables = parseTemplateVariableNames(value);

        // Check if this StringLiteral contains our target variable
        if (foundVariables.includes(variableName) && n.node.parent?.type.is(LabelMatcher)) {
          // The label name is the sibling before the operator
          // Structure: LabelMatcher -> [LabelName, MatchOp, StringLiteral]
          const labelNameNode = n.node.prevSibling?.prevSibling;
          if (labelNameNode) {
            const labelName = getNodeText(labelNameNode, query);
            if (labelName) {
              labelNames.add(labelName);
            }
          }
        }
      }
    },
  });

  return Array.from(labelNames);
}

function getVariableNameFromString(str: string) {
  const variables = parseTemplateVariableNames(str);
  return variables?.[0];
}

/**
 * This function will return the text of a given node from a string
 */
function getNodeText(node: SyntaxNodeRef, str: string) {
  return str.substring(node.from, node.to);
}

// This function will return a default variable config for a given variable name
function defaultVariableConfig(name: string): TextVariableDefinition {
  return {
    kind: 'TextVariable',
    spec: {
      name: name,
      value: '',
    },
  };
}

function createStepIntervalVariable(variableName: string): ListVariableDefinition {
  return {
    kind: 'ListVariable',
    spec: {
      allow_all_value: false,
      allow_multiple: false,
      name: variableName,
      plugin: {
        kind: 'StaticListVariable',
        spec: {
          values: [
            {
              value: '15s',
              label: '15s',
            },
            {
              value: '30s',
              label: '30s',
            },
            {
              value: '1m',
              label: '1m',
            },
            {
              value: '2m',
              label: '2m',
            },
            {
              value: '5m',
              label: '5m',
            },
          ],
        },
      },
    },
  };
}

function createPromLabelValuesVariable(
  variableName: string,
  labelName: string,
  options?: { matchers?: string[]; isMulti?: boolean }
): ListVariableDefinition {
  const { matchers = [], isMulti = false } = options || {};
  const allowAll = isMulti ? true : false;
  return {
    kind: 'ListVariable',
    spec: {
      allow_all_value: allowAll,
      allow_multiple: isMulti,
      custom_all_value: allowAll ? '.*' : undefined,
      default_value: allowAll ? '$__all' : undefined,
      name: variableName,
      plugin: {
        kind: 'PrometheusLabelValuesVariable',
        spec: {
          label_name: labelName,
          matchers: matchers,
        } as PrometheusLabelValuesVariableOptions,
      },
    },
  };
}

function createPromLabelNamesVariable(variableName: string, matchers: string[]): ListVariableDefinition {
  return {
    kind: 'ListVariable',
    spec: {
      allow_all_value: false,
      allow_multiple: false,
      name: variableName,
      plugin: {
        kind: 'PrometheusLabelNamesVariable',
        spec: {
          matchers,
        } as PrometheusLabelNamesVariableOptions,
      },
    },
  };
}

export async function injectGlobalFilters(
  query: string,
  filters: GlobalFilter[],
  args: {
    replaceExistingLabels?: boolean;
  } = {}
): Promise<{ query: string; warnings: string[] }> {
  return injectGlobalFiltersSync(query, filters, args);
}

/**
 * This function will inject global filters into a given PromQL query
 * @param query The PromQL query to inject the global filters into
 * @param filters The global filters to inject into the query
 * @param args Additional arguments
 * @param args.replaceExistingLabels If true, the function will replace any existing labels that match the global filters
 */
export function injectGlobalFiltersSync(
  query: string,
  filters: GlobalFilter[],
  args: {
    replaceExistingLabels?: boolean;
  } = {}
): { query: string; warnings: string[] } {
  if (filters.length === 0) {
    return {
      query,
      warnings: [],
    };
  }

  const { replaceExistingLabels = false } = args;
  const warnings: string[] = [];
  const stringEdits: Edit[] = [];

  const ast = parser.parse(query);

  // Remove any existing labels that match the global filters to avoid conflicts
  // TODO: In the future, we may want to modify the original filter instead of removing it
  ast.iterate({
    enter(n) {
      if (n.type.is(LabelMatcher)) {
        const text = getNodeText(n, query);
        filters.forEach((f) => {
          if (text.startsWith(maybeQuoteLabelName(f.label))) {
            if (replaceExistingLabels) {
              // remove the filter
              warnings.push(`Label "${text}" already exists in the query. Overwriting value with "${f.value}"`);
              stringEdits.push({ position: n.from, length: n.to - n.from + 1 });
            } else {
              // The global filter will be added as an additional matcher, but make sure we add a warning
              warnings.push(`Label "${text}" already exists in the query. Matchers may conflict "${f.label}"`);
            }
          }
        });
      }
    },
  });

  const newGlobalFilterString = getPromMatcherFromGlobalFilters(filters);

  // Insert the global filters into the query
  ast.iterate({
    enter(n) {
      if (n.type.is(VectorSelector)) {
        // No label matcher
        const labelMatcher = n.node.getChild(LabelMatchers);
        if (!labelMatcher) {
          stringEdits.push({ position: n.to, string: `{${newGlobalFilterString}}` });
        } else {
          // Check to see if the last char in the matcher is a comma (irrespective of whitespace) because adding a
          // comma in front of the global filter in that case will result in invalid syntax
          const labelMatcherText = getNodeText(labelMatcher, query);
          const hasTrailingComma = /\s*,\s*}/.test(labelMatcherText);

          if (!labelMatcher.firstChild || hasTrailingComma) {
            stringEdits.push({ position: labelMatcher.to - 1, string: `${newGlobalFilterString}` });
          } else {
            stringEdits.push({ position: labelMatcher.to - 1, string: `,${newGlobalFilterString}` });
          }
        }
      }
    },
  });

  const newQuery = applyEditsToString(query, stringEdits);

  if (stringEdits.length === 0) {
    warnings.push('No vector selectors found in query. Global filters were not injected.');
    return {
      query,
      warnings,
    };
  }

  return {
    query: newQuery,
    warnings,
  };
}

/**
 * This function will inject group by labels into a given PromQL query
 */
export function injectGroupByLabels(
  query: string,
  labels: string[],
  options?: {
    /**
     * Skips injecting group by labels if there is no aggregation function in the query
     */
    skipIfNoAgg: boolean;
  }
) {
  const { skipIfNoAgg = true } = options || {};
  const ast = parser.parse(query);
  const stringEdits: Edit[] = [];
  const aggType = 'sum';

  if (labels.length === 0) {
    return query;
  }

  ast.iterate({
    enter(n) {
      if (n.type.is(VectorSelector)) {
        const aggExpressionNode = walkBackward(n.node, AggregateExpr);
        if (aggExpressionNode) {
          const aggModifiedNode = aggExpressionNode.getChild(AggregateModifier);
          // If it has a modifier, we need to insert the labels with the existing modifier
          if (aggModifiedNode) {
            // If the modifier doesn't contain "without" we want to add the labels to the modifier
            if (!aggModifiedNode.getChild(Without)) {
              const groupingLabels = aggModifiedNode.getChild(GroupingLabels);
              if (groupingLabels) {
                let prefix = '';
                const groupingLabelText = getNodeText(groupingLabels, query);
                const hasTrailingComma = /\s*,\s*\)/.test(groupingLabelText);
                const hasLabels = groupingLabels.firstChild?.type.is(LabelName);
                if (!hasTrailingComma && hasLabels) {
                  prefix = ',';
                }
                stringEdits.push({
                  position: aggModifiedNode.to - 1,
                  string: `${prefix}${labels.map(maybeQuoteLabelName).join(',')}`,
                });
              }
            }
          } else {
            stringEdits.push({
              position: aggExpressionNode.to,
              string: ` by (${labels.map(maybeQuoteLabelName).join(',')})`,
            });
          }
        } else if (!skipIfNoAgg) {
          let nodeToModify = n;

          // Check to see if we are in a rate style function like `rate(foo[1m])`
          const upperParent = n.node.parent?.parent?.parent;
          if (upperParent && upperParent.type.is(FunctionCall) && n.node.parent?.type.is(MatrixSelector)) {
            nodeToModify = upperParent;
          }

          stringEdits.push({ position: nodeToModify.from, string: `${aggType}(` });
          stringEdits.push({
            position: nodeToModify.to,
            string: `) by (${labels.map(maybeQuoteLabelName).join(',')})`,
          });
        }
      }
    },
  });

  return applyEditsToString(query, stringEdits);
}

function getPromMatcherFromGlobalFilter(f: GlobalFilter) {
  // Even though we're using regex matcher in the prom query, we only
  // allow for exact matches, so we can safely escape any regex characters
  const matcher = f.matcher === '=' ? '=~' : '!~';
  const values = Array.isArray(f.value) ? f.value : [f.value];
  const escaped = values.map(escapeForPromQLRegex);
  return `${maybeQuoteLabelName(f.label)}${matcher}\`(|${escaped.join('|')})\``;
}

function getPromMatcherFromGlobalFilters(filters: GlobalFilter[]) {
  return filters.map(getPromMatcherFromGlobalFilter).join(',');
}

function escapeForPromQLRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * String helpers
 */
interface Edit {
  position: number;
  length?: number; // Length of characters to delete. Undefined or 0 means no deletion.
  string?: string; // String to insert. Undefined means no insertion.
}

/**
 * This function will apply multiple edits (insertions or deletions) to a given string
 */
function applyEditsToString(originalString: string, edits: Edit[]): string {
  // Sort edits by position in descending order to avoid messing up indices
  edits.sort((a, b) => b.position - a.position);

  // Perform each edit
  edits.forEach((edit) => {
    const { position, length = 0, string } = edit;

    // Handle deletion
    if (length > 0) {
      originalString = originalString.slice(0, position) + originalString.slice(position + length);
    }

    // Handle insertion
    if (string) {
      originalString = originalString.slice(0, position) + string + originalString.slice(position);
    }
  });

  return originalString;
}

/**
 * Injects a variable reference into range vector durations in a PromQL query.
 * For example: `rate(http_requests_total[5m])` becomes `rate(http_requests_total[$interval])`
 * @param query The PromQL query to inject the variable into
 * @param variableName The variable name (without $ prefix)
 * @returns The modified query string
 */
function injectVariableIntoRangeVector(query: string, variableName: string): string {
  if (!query || !variableName) {
    return query;
  }

  const ast = parser.parse(query);
  const stringEdits: Edit[] = [];
  const variableReference = `$${variableName}`;

  ast.iterate({
    enter(n) {
      if (n.type.is(MatrixSelector)) {
        // Check for existing Duration or TemplateVariable within the MatrixSelector
        const durationNode = n.node.getChild(Duration);
        const variableNode = n.node.getChild(TemplateVariable);

        if (durationNode) {
          // Replace the duration with our variable reference
          stringEdits.push({
            position: durationNode.from,
            length: durationNode.to - durationNode.from,
            string: variableReference,
          });
        } else if (variableNode) {
          // There's already a variable in the range vector
          const existingVar = getNodeText(variableNode, query);
          // Only replace if it's a different variable
          if (existingVar !== variableReference) {
            stringEdits.push({
              position: variableNode.from,
              length: variableNode.to - variableNode.from,
              string: variableReference,
            });
          }
        }
      }
    },
  });

  if (stringEdits.length === 0) {
    // No MatrixSelectors with durations found, return original query
    return query;
  }

  return applyEditsToString(query, stringEdits);
}

/**
 * Injects a variable reference as a label matcher into a PromQL query.
 * For example: `up` becomes `up{instance=~"$instance"}`
 * If the label already exists in the query, it will be replaced with the variable.
 * For example: `up{instance="foo"}` becomes `up{instance=~"$instance"}`
 * @param query The PromQL query to inject the variable into
 * @param options Configuration for the variable injection (must be LabelMatcherInjection)
 * @returns The modified query string
 */
function injectVariableAsLabelMatcher(query: string, options: LabelMatcherInjection): string {
  const { name, labelName, operator = DEFAULT_INJECT_OPERATOR } = options;

  if (!query || !name || !labelName) {
    return query;
  }

  const ast = parser.parse(query);
  const stringEdits: Edit[] = [];

  // Track which VectorSelectors will have all their matchers removed
  const vectorSelectorsWithAllMatchersRemoved = new Set<number>();

  // First pass: Remove any existing matchers for this label to avoid conflicts
  // We need to track commas to maintain valid syntax
  ast.iterate({
    enter(n) {
      if (n.type.is(LabelMatcher)) {
        const text = getNodeText(n, query);
        // Check if this matcher is for our target label
        // Match patterns like: labelName=, labelName!=, labelName=~, labelName!~
        // Use maybeQuoteLabelName to handle UTF-8 label names that get quoted
        const quotedLabelName = maybeQuoteLabelName(labelName);
        if (text.startsWith(`${quotedLabelName}=`) || text.startsWith(`${quotedLabelName}!`)) {
          // Determine if there are other matchers (siblings)
          const hasPrevSibling = n.node.prevSibling !== null;
          const hasNextSibling = n.node.nextSibling !== null;

          if (!hasPrevSibling && !hasNextSibling) {
            // This is the only matcher, remove it and any trailing comma
            let endPos = n.to;
            // Check if there's a comma after this matcher (even with whitespace)
            let checkPos = endPos;
            while (checkPos < query.length) {
              const char = query[checkPos];
              if (!char || !/\s/.test(char)) {
                break;
              }
              checkPos++;
            }
            if (checkPos < query.length && query[checkPos] === ',') {
              // Include the trailing comma in the deletion
              endPos = checkPos + 1;
            }
            stringEdits.push({ position: n.from, length: endPos - n.from });
            // Mark this VectorSelector as having all matchers removed
            const vectorSelector = walkBackward(n.node, VectorSelector);
            if (vectorSelector) {
              vectorSelectorsWithAllMatchersRemoved.add(vectorSelector.from);
            }
          } else if (!hasPrevSibling && hasNextSibling) {
            // This is the first of multiple matchers, remove it and the trailing comma
            // Find the comma after this matcher
            let commaPos = n.to;
            while (commaPos < query.length) {
              const char = query[commaPos];
              if (!char || !/\s/.test(char)) {
                break;
              }
              commaPos++;
            }
            if (commaPos < query.length && query[commaPos] === ',') {
              // Include any whitespace after the comma
              let endPos = commaPos + 1;
              while (endPos < query.length) {
                const char = query[endPos];
                if (!char || !/\s/.test(char)) {
                  break;
                }
                endPos++;
              }
              stringEdits.push({ position: n.from, length: endPos - n.from });
            } else {
              // No comma found, just remove the matcher
              stringEdits.push({ position: n.from, length: n.to - n.from });
            }
          } else {
            // This is not the first matcher, remove the leading comma and the matcher
            // Look backwards to find the comma
            let commaPos = n.from - 1;
            while (commaPos >= 0) {
              const char = query[commaPos];
              if (!char || !/\s/.test(char)) {
                break;
              }
              commaPos--;
            }
            if (commaPos >= 0 && query[commaPos] === ',') {
              stringEdits.push({ position: commaPos, length: n.to - commaPos });
            } else {
              // Just remove the matcher if we can't find the comma
              stringEdits.push({ position: n.from, length: n.to - n.from });
            }
          }
        }
      }
    },
  });

  // Create the label matcher string: labelName=~"$variableName"
  const variableMatcherString = `${maybeQuoteLabelName(labelName)}${operator}"$${name}"`;

  // Second pass: Insert the variable into every VectorSelector in the query
  ast.iterate({
    enter(n) {
      if (n.type.is(VectorSelector)) {
        const labelMatcher = n.node.getChild(LabelMatchers);
        if (!labelMatcher) {
          // No existing label matchers, add new ones
          stringEdits.push({ position: n.to, string: `{${variableMatcherString}}` });
        } else {
          // Check if we removed all matchers from this VectorSelector
          const allMatchersRemoved = vectorSelectorsWithAllMatchersRemoved.has(n.from);

          // Check to see if the last char in the matcher is a comma (irrespective of whitespace)
          const labelMatcherText = getNodeText(labelMatcher, query);
          const hasTrailingComma = /\s*,\s*}/.test(labelMatcherText);

          if (!labelMatcher.firstChild || hasTrailingComma || allMatchersRemoved) {
            stringEdits.push({ position: labelMatcher.to - 1, string: `${variableMatcherString}` });
          } else {
            stringEdits.push({ position: labelMatcher.to - 1, string: `,${variableMatcherString}` });
          }
        }
      }
    },
  });

  if (stringEdits.length === 0) {
    // No VectorSelectors found, return original query
    return query;
  }

  return applyEditsToString(query, stringEdits);
}

/**
 * Injects a variable reference into a PromQL query.
 * Supports two injection types:
 * - 'LabelMatcher': Injects as a label matcher, e.g., up{instance=~"$var"}
 * - 'RangeVector': Injects into range vector duration, e.g., rate(metric[5m]) -> rate(metric[$var])
 * @param query The PromQL query to inject the variable into
 * @param options Configuration for the variable injection
 * @returns The modified query string
 */
export function injectVariableIntoPromQLQuery(query: string, options: InjectVariableOptions): string {
  if (options.injectionType === 'RangeVector') {
    return injectVariableIntoRangeVector(query, options.name);
  }
  return injectVariableAsLabelMatcher(query, options);
}
